"use client"

import * as React from "react"
import {
  Check,
  HeartPulse,
  Building2,
  Users,
  Globe,
  Shield,
  Hospital,
  Stethoscope,
  Pencil,
  Eye,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { type PlanDisplayData } from "@/lib/plan-display-data"
import { useCompareStore } from "@/hooks/use-compare"
import { ConfigureBenefitsModal } from "@/components/plan/ConfigureBenefitsModal"
import { ViewAllModal } from "@/components/plan/ViewAllModal"
import { QuoteConfirmModal } from "@/components/plan/QuoteConfirmModal"
import { PriceDetailModal } from "@/components/plan/PriceDetailModal"
import { useAuthStore, useCurrentUser } from "@/hooks/use-auth"
import { useAuthModalStore } from "@/hooks/use-auth-modal"
import { usePermissions } from "@/hooks/use-permissions"
import { useParams, useRouter } from "next/navigation"
import { ROUTES } from "@/lib/routes"
import { useVisitorQuotesStore } from "@/stores/visitor-quotes"

interface PlanComparisonCardProps {
  plan: PlanDisplayData
  isHighlighted?: boolean
  paymentFrequency: "monthly" | "yearly"
  onFrequencyChange?: (freq: "monthly" | "yearly") => void
  onCompareToggle?: () => void
  onShortlistToggle?: () => void
  onBuyClick?: () => void
  categoryBadge?: string
  memberCount?: number
  isSMEQuote?: boolean
}

const PLAN_ICONS: Record<string, React.ReactNode> = {
  "nova-care-regional": <HeartPulse className="h-6 w-6 text-nova-600" />,
  "nova-biz-essential": <Building2 className="h-6 w-6 text-nova-600" />,
  "nova-corp-health": <Users className="h-6 w-6 text-nova-600" />,
  "nova-global-individual": <Globe className="h-6 w-6 text-nova-600" />,
  "nova-global-sme": <Shield className="h-6 w-6 text-nova-600" />,
}

const PLAN_BADGE_COLORS: Record<string, string> = {
  "Instant Issuance": "bg-slate-600 text-white",
  "Limited Pay Term": "bg-emerald-600 text-white",
  "Price on Request": "bg-slate-500 text-white",
}

export function PlanComparisonCard({
  plan,
  isHighlighted = false,
  paymentFrequency,
  onBuyClick,
  categoryBadge,
  memberCount = 1,
  isSMEQuote = false,
}: PlanComparisonCardProps) {
  const comparePlans = useCompareStore((s) => s.plans)
  const addPlan = useCompareStore((s) => s.addPlan)
  const { contactQuote } = useVisitorQuotesStore()
  const { isRole } = usePermissions()
  const isInCompare = comparePlans.some((p) => p.slug === plan.slug)
  const tokens = useAuthStore((s) => s.tokens)
  const user = useCurrentUser()
  const openAuthModal = useAuthModalStore((s) => s.openModal)
  const router = useRouter()
  const { locale: localeParam } = useParams()
  const locale = (localeParam as string) ?? "en"
  // Hide detailed pricing from brokers, agents, and regular customers
  const canViewDetailedPricing = user?.role
    ? ["admin", "super_admin", "sales"].includes(user.role)
    : false
  const currentPrice =
    paymentFrequency === "yearly" ? plan.yearlyPrice : plan.monthlyPrice

  const [configValues, setConfigValues] = React.useState<
    Record<string, string>
  >({})
  const [showEditModal, setShowEditModal] = React.useState(false)
  const [showViewAllModal, setShowViewAllModal] = React.useState(false)
  const [showQuoteModal, setShowQuoteModal] = React.useState(false)
  const [showPriceDetailModal, setShowPriceDetailModal] = React.useState(false)

  // No mock data - cardFields is empty until real data is loaded

  function handleBuyClick() {
    if (onBuyClick) {
      onBuyClick()
      return
    }
    if (!tokens?.access_token) {
      openAuthModal("login", "buy-plan", plan.slug, () => {
        setShowQuoteModal(true)
      })
      return
    }
    setShowQuoteModal(true)
  }

  function handleViewAllClick() {
    if (!tokens?.access_token) {
      openAuthModal("login", "view-plan", plan.slug, () => {
        router.push(ROUTES.planDetail(locale, plan.slug))
      })
      return
    }
    router.push(ROUTES.planDetail(locale, plan.slug))
  }

  return (
    <>
      <div
        className={cn(
          "relative rounded-xl border bg-white transition-all dark:border-slate-700 dark:bg-slate-900",
          isHighlighted
            ? "border-nova-500 ring-2 ring-nova-500/30"
            : "border-slate-200 hover:border-slate-300 dark:border-slate-700"
        )}
      >
        {/* Plan badge (Instant Issuance etc.) */}
        {plan.badge && (
          <div
            className={cn(
              "absolute -top-2.5 left-4 rounded px-2.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase",
              PLAN_BADGE_COLORS[plan.badge] || "bg-slate-600 text-white"
            )}
          >
            {plan.badge}
          </div>
        )}

        {/* SME category badge */}
        {categoryBadge && (
          <div className="absolute -top-2.5 right-4 rounded bg-nova-600 px-2.5 py-0.5 text-[10px] font-semibold tracking-wide text-white uppercase">
            {categoryBadge}
          </div>
        )}

        <div className="flex">
          {/* ── Left sidebar ── */}
          <div className="flex w-44 shrink-0 flex-col items-start gap-3 border-r border-slate-100 p-4 dark:border-slate-700/60">
            <div className="flex cursor-pointer items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800">
                {PLAN_ICONS[plan.slug] || (
                  <HeartPulse className="h-5 w-5 text-nova-600" />
                )}
              </div>
              <div className="min-w-0">
                <h3 className="text-xs leading-tight font-bold text-slate-900 dark:text-white">
                  {plan.name}
                </h3>
                <p className="text-[10px] text-slate-400 dark:text-slate-500">
                  {isSMEQuote ? plan.productName : null}
                </p>
              </div>
            </div>
          </div>

          {/* ── Main content ── */}
          <div className="flex min-w-0 flex-1 flex-col">
            {/* Meta row: TPA / Annual Limit / Coverage / Buy CTA */}
            <div className="flex items-stretch border-b border-slate-100 dark:border-slate-700/60">
              <div className="flex flex-col justify-center border-x border-slate-100 bg-slate-50 px-4 py-3 dark:border-slate-700/60 dark:bg-slate-800/40">
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  Annual Medical Limit:
                </p>
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {configValues["annualMedicalLimit"] ?? plan.annualMedicalLimit}
                </p>
              </div>
              <div className="flex flex-col justify-center px-4 py-3">
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  TPA :
                </p>
                <p className="text-xs font-bold text-slate-900 dark:text-white">
                  {plan.tpa}
                </p>
              </div>
              <div className="flex flex-col justify-center bg-slate-50 px-4 py-3 dark:bg-slate-800/40">
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  Coverage IP:
                </p>
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {plan.selectedIpNetwork ?? plan.planTier?.networkIp}
                </p>
              </div>
              <div className="flex flex-col justify-center bg-slate-50 px-4 py-3 dark:bg-slate-800/40">
                <p className="text-[10px] text-slate-500 dark:text-slate-400">
                  Coverage OP:
                </p>
                <p className="text-sm font-bold text-slate-900 dark:text-white">
                  {plan.selectedOpNetwork ?? plan.planTier?.networkOp}
                </p>
              </div>
              {/* Buy / Get Quote + frequency toggle */}
              <div className="ml-auto flex flex-col items-end justify-center gap-2 border-l border-slate-100 px-4 py-3 dark:border-slate-700/60">
                {currentPrice ? (
                  <div className="group relative">
                    <button
                      onClick={handleBuyClick}
                      className="cursor-pointer rounded-lg bg-slate-800 px-4 py-2 text-left text-white transition-colors hover:bg-slate-700"
                    >
                      <span className="block text-[10px] font-medium text-white">
                        Buy @{" "}
                        <span className="text-sm font-bold">
                          {currentPrice.toLocaleString()}{" "}
                          {plan.priceBreakdown?.currency ?? "AED"}
                        </span>
                      </span>
                    </button>
                    {plan.priceBreakdown && canViewDetailedPricing && (
                      <div
                        role="tooltip"
                        className="absolute top-full right-0 z-50 mt-2 hidden max-h-[80vh] w-96 overflow-y-auto rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700 shadow-2xl group-hover:block hover:block dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
                      >
                        <p className="mb-2 text-[11px] font-semibold tracking-wide text-nova-600 uppercase dark:text-nova-400">
                          Price Breakdown
                        </p>
                        <dl className="space-y-1">
                          <div className="flex justify-between">
                            <dt>Gross premium</dt>
                            <dd className="font-medium">
                              {plan.priceBreakdown.totalGross.toLocaleString(
                                undefined,
                                { maximumFractionDigits: 2 }
                              )}
                            </dd>
                          </div>
                          {plan.priceBreakdown.basmah &&
                            plan.priceBreakdown.basmah > 0 && (
                              <div className="flex justify-between text-slate-500 dark:text-slate-400">
                                <dt>
                                  + Basmah
                                  {plan.priceBreakdown.basmahMemberCount
                                    ? ` (${plan.priceBreakdown.basmahMemberCount} members)`
                                    : ""}
                                </dt>
                                <dd className="font-medium">
                                  {plan.priceBreakdown.basmah.toLocaleString(
                                    undefined,
                                    { maximumFractionDigits: 2 }
                                  )}
                                </dd>
                              </div>
                            )}
                          {plan.priceBreakdown.icp &&
                            plan.priceBreakdown.icp > 0 && (
                              <div className="flex justify-between text-slate-500 dark:text-slate-400">
                                <dt>
                                  + ICP
                                  {plan.priceBreakdown.icpMemberCount
                                    ? ` (${plan.priceBreakdown.icpMemberCount} members)`
                                    : ""}
                                </dt>
                                <dd className="font-medium">
                                  {plan.priceBreakdown.icp.toLocaleString(
                                    undefined,
                                    { maximumFractionDigits: 2 }
                                  )}
                                </dd>
                              </div>
                            )}
                          <div className="flex justify-between text-slate-500 dark:text-slate-400">
                            <dt>+ VAT (5%)</dt>
                            <dd className="font-medium">
                              {plan.priceBreakdown.vat.toLocaleString(
                                undefined,
                                { maximumFractionDigits: 2 }
                              )}
                            </dd>
                          </div>
                          <div className="flex justify-between border-t border-slate-200 pt-1 text-sm font-semibold text-slate-900 dark:border-slate-700 dark:text-white">
                            <dt>Total payable</dt>
                            <dd>
                              {plan.priceBreakdown.totalWithVat.toLocaleString(
                                undefined,
                                { maximumFractionDigits: 2 }
                              )}{" "}
                              {plan.priceBreakdown.currency}
                            </dd>
                          </div>
                        </dl>
                        {/* Loadings breakdown — hidden per product request; restore by un-commenting.
                        {plan.priceBreakdown.loadingBreakdown &&
                          plan.priceBreakdown.loadingBreakdown.length > 0 && (
                            <>
                              <p className="mt-3 mb-1 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                                Loadings breakdown
                              </p>
                              <ul className="space-y-0.5">
                                {plan.priceBreakdown.loadingBreakdown.map(
                                  (l) => (
                                    <li
                                      key={l.party}
                                      className="flex justify-between gap-3"
                                    >
                                      <span className="text-slate-500 capitalize dark:text-slate-400">
                                        {l.party.replace(/_/g, " ")}
                                      </span>
                                      <span className="font-medium">
                                        {l.percentage}%
                                      </span>
                                    </li>
                                  )
                                )}
                              </ul>
                            </>
                          )}
                        */}
                        {plan.priceBreakdown.optionalBenefits &&
                          plan.priceBreakdown.optionalBenefits.length > 0 && (
                            <>
                              <p className="mt-3 mb-1 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                                Configure Benefits — added cost
                              </p>
                              <ul className="space-y-0.5">
                                {plan.priceBreakdown.optionalBenefits.map(
                                  (ob) => {
                                    const isZero = ob.rate === 0
                                    const reason =
                                      isZero && ob.benefitType === "maternity"
                                        ? "no eligible member"
                                        : isZero
                                          ? "no contribution"
                                          : null
                                    return (
                                      <li
                                        key={ob.benefitType}
                                        className="flex justify-between gap-3"
                                      >
                                        <span className="text-slate-500 capitalize dark:text-slate-400">
                                          {ob.benefitType.replace(/_/g, " ")}
                                          {ob.label ? (
                                            <span className="ml-1 text-slate-400">
                                              ({ob.label})
                                            </span>
                                          ) : null}
                                          {reason ? (
                                            <span className="ml-1 text-[10px] text-slate-400 italic">
                                              — {reason}
                                            </span>
                                          ) : null}
                                        </span>
                                        <span
                                          className={
                                            isZero
                                              ? "font-medium text-slate-400"
                                              : "font-medium"
                                          }
                                        >
                                          {ob.rate.toLocaleString(undefined, {
                                            maximumFractionDigits: 2,
                                          })}
                                        </span>
                                      </li>
                                    )
                                  }
                                )}
                              </ul>
                            </>
                          )}
                        {plan.priceBreakdown.pricingMembers &&
                        plan.priceBreakdown.pricingMembers.length > 0 ? (
                          <>
                            <p className="mt-3 mb-1 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                              Per-member calculation
                            </p>
                            <ul className="space-y-1">
                              {plan.priceBreakdown.pricingMembers.map(
                                (m, i) => {
                                  const meta = [
                                    m.relationship
                                      ? m.relationship.charAt(0).toUpperCase() +
                                        m.relationship.slice(1)
                                      : null,
                                    m.age != null ? `${m.age}y` : null,
                                    m.gender,
                                    m.visaEmirate
                                      ? m.visaEmirate.toUpperCase()
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")
                                  const fmt = (n: number) =>
                                    n.toLocaleString(undefined, {
                                      maximumFractionDigits: 2,
                                    })
                                  return (
                                    <li key={i}>
                                      <details className="group/m rounded border border-slate-100 dark:border-slate-800">
                                        <summary className="flex cursor-pointer list-none items-baseline justify-between gap-3 p-2 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                          <span className="flex items-baseline gap-1">
                                            <span className="text-[10px] text-slate-400 transition-transform group-open/m:rotate-90">
                                              ▶
                                            </span>
                                            <span className="font-semibold text-slate-700 dark:text-slate-200">
                                              {m.name}
                                            </span>
                                            {meta ? (
                                              <span className="ml-1 text-[10px] text-slate-400">
                                                {meta}
                                              </span>
                                            ) : null}
                                          </span>
                                          <span className="text-[11px] font-semibold text-nova-600 dark:text-nova-400">
                                            {fmt(m.finalPremium)}
                                          </span>
                                        </summary>
                                        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 border-t border-slate-100 p-2 text-[10px] text-slate-500 dark:border-slate-800 dark:text-slate-400">
                                          <span>Base rate</span>
                                          <span className="text-right font-mono">
                                            {fmt(m.baseRate)}
                                          </span>
                                          <span>× Adj factor</span>
                                          <span className="text-right font-mono">
                                            {m.adjustmentFactor.toLocaleString(
                                              undefined,
                                              { maximumFractionDigits: 4 }
                                            )}
                                          </span>
                                          <span className="text-slate-600 dark:text-slate-300">
                                            = Modified base
                                          </span>
                                          <span className="text-right font-mono text-slate-600 dark:text-slate-300">
                                            {fmt(m.modifiedBase)}
                                          </span>
                                          <span>+ Optional</span>
                                          <span className="text-right font-mono">
                                            {fmt(m.optionalPremium)}
                                          </span>
                                          <span className="text-slate-600 dark:text-slate-300">
                                            = Net rate
                                          </span>
                                          <span className="text-right font-mono text-slate-600 dark:text-slate-300">
                                            {fmt(m.netRate)}
                                          </span>
                                          {m.memberFees?.trudoc != null && m.memberFees.trudoc > 0 && (
                                            <>
                                              <span className="text-teal-600 dark:text-teal-400">+ Tru Doc</span>
                                              <span className="text-right font-mono text-teal-600 dark:text-teal-400">
                                                {fmt(m.memberFees.trudoc)}
                                              </span>
                                              <span className="text-teal-700 dark:text-teal-300">= Net Inclusive</span>
                                              <span className="text-right font-mono text-teal-700 dark:text-teal-300">
                                                {fmt(m.netRateInclusive ?? (m.netRate + (m.memberFees?.trudoc ?? 0)))}
                                              </span>
                                            </>
                                          )}
                                          <span>+ Basma/ICP</span>
                                          <span className="text-right font-mono">
                                            {fmt(m.basmaOrIcp)}
                                          </span>
                                        </div>
                                      </details>
                                    </li>
                                  )
                                }
                              )}
                            </ul>
                          </>
                        ) : plan.priceBreakdown.members &&
                          plan.priceBreakdown.members.length > 0 ? (
                          <>
                            <p className="mt-3 mb-1 text-[11px] font-semibold tracking-wide text-slate-500 uppercase">
                              Per-member net rate
                            </p>
                            <ul className="space-y-0.5">
                              {plan.priceBreakdown.members.map((m, i) => {
                                const meta = [
                                  m.relationship
                                    ? m.relationship.charAt(0).toUpperCase() +
                                      m.relationship.slice(1)
                                    : null,
                                  m.age != null ? `${m.age}y` : null,
                                  m.gender,
                                  m.visaEmirate
                                    ? m.visaEmirate.toUpperCase()
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")
                                return (
                                  <li
                                    key={i}
                                    className="flex justify-between gap-3"
                                  >
                                    <span className="text-slate-500 dark:text-slate-400">
                                      <span className="font-medium text-slate-700 dark:text-slate-300">
                                        {m.name ?? `Member ${i + 1}`}
                                      </span>
                                      {meta ? (
                                        <span className="ml-1 text-[10px] text-slate-400">
                                          {meta}
                                        </span>
                                      ) : null}
                                    </span>
                                    <span className="font-medium whitespace-nowrap">
                                      {m.rate.toLocaleString(undefined, {
                                        maximumFractionDigits: 2,
                                      })}
                                    </span>
                                  </li>
                                )
                              })}
                            </ul>
                          </>
                        ) : null}
                        <p className="mt-2 text-[10px] text-slate-400 italic">
                          Basma/ICP per-member fees are part of Loadings.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={handleBuyClick}
                    className="cursor-pointer rounded-lg border border-nova-600 px-4 py-2 text-xs font-semibold text-nova-600 transition-colors hover:bg-nova-50 dark:hover:bg-nova-600/10"
                  >
                    Get Quote
                  </button>
                )}
              </div>
            </div>

            {/* Optional Benefits — dynamic from API */}
            {plan.benefits && Object.keys(plan.benefits).length > 0 && (
              <div className="p-4">
                <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                  {Object.entries(plan.benefits).map(([key, value]) => (
                    <div key={key} className="text-xs">
                      <span className="text-slate-500 dark:text-slate-400">
                        {key
                          .replace(/_/g, " ")
                          .replace(/\b\w/g, (c) => c.toUpperCase())}
                        {": "}
                      </span>
                      <span className="font-bold text-slate-900 dark:text-white">
                        {value || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Adjustment Factors — dynamic from API */}
            {((plan.adjustmentFactors && plan.adjustmentFactors.length > 0) ||
              (plan.coverageArea &&
                plan.productCode?.startsWith("micro_sme"))) && (
              <div className="border-t border-slate-100 px-4 py-3 dark:border-slate-700/60">
                <p className="mb-2 text-[10px] font-semibold tracking-wide text-slate-400 uppercase">
                  Adjustment Options
                </p>
                <div className="space-y-1.5">
                  {plan.coverageArea &&
                    plan.productCode?.startsWith("micro_sme") && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500 dark:text-slate-400">
                          Territory
                        </span>
                        <span className="font-medium text-slate-700 dark:text-slate-300">
                          {plan.coverageArea}
                        </span>
                      </div>
                    )}
                  {plan.adjustmentFactors &&
                    plan.adjustmentFactors.map((af) => {
                      const selectedOpt = af.options.find(
                        (o) => o.id === af.selectedId
                      )
                      const displayOpt =
                        selectedOpt ?? af.options.find((o) => o.isDefault)
                      return (
                        <div
                          key={af.factorType}
                          className="flex items-center justify-between text-xs"
                        >
                          <span className="text-slate-500 dark:text-slate-400">
                            {af.factorType
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (c) => c.toUpperCase())}
                          </span>
                          <span className="font-medium text-slate-700 dark:text-slate-300">
                            {displayOpt?.optionLabel?.replace(/%/g, "") ?? "—"}
                          </span>
                        </div>
                      )
                    })}
                </div>
              </div>
            )}

            {/* Bottom action row */}
            <div className="mt-auto flex items-center justify-between border-t border-slate-100 px-4 py-2.5 dark:border-slate-700/60">
              {/* Plan Features : Edit | View all */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setShowEditModal(true)}
                  className="flex cursor-pointer items-center gap-1 text-[13px] font-semibold text-nova-600 transition-colors hover:text-nova-700 dark:text-white"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
                <span className="text-slate-200 dark:text-slate-700">|</span>
                <button
                  onClick={handleViewAllClick}
                  className="flex cursor-pointer items-center gap-1 text-[13px] font-semibold text-nova-600 transition-colors hover:text-nova-700 dark:text-white"
                >
                  <Eye className="h-3 w-3" />
                  View Plan Details
                </button>
                {plan.priceBreakdown?.fullMembers &&
                  plan.priceBreakdown.fullMembers.length > 0 &&
                  isRole("super_admin", "admin", "sales") && (
                    <>
                      <span className="text-slate-200 dark:text-slate-700">
                        |
                      </span>
                      <button
                        onClick={() => setShowPriceDetailModal(true)}
                        className="flex cursor-pointer items-center gap-1 text-[13px] font-semibold text-emerald-600 transition-colors hover:text-emerald-700 dark:text-emerald-400"
                      >
                        <Eye className="h-3 w-3" />
                        Price Detail
                      </button>
                    </>
                  )}
              </div>

              <label className="flex cursor-pointer items-center gap-1.5 select-none">
                <div
                  onClick={() => addPlan(plan)}
                  className={cn(
                    "flex h-4 w-4 items-center justify-center rounded border transition-all",
                    isInCompare
                      ? "border-nova-600 bg-nova-600"
                      : "border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-800"
                  )}
                >
                  {isInCompare && <Check className="h-2.5 w-2.5 text-white" />}
                </div>
                <span className="text-xs text-slate-500 dark:text-white">
                  Compare
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <ConfigureBenefitsModal
          plan={plan}
          configValues={configValues}
          onSave={setConfigValues}
          onClose={() => setShowEditModal(false)}
        />
      )}
      {showViewAllModal && (
        <ViewAllModal
          plan={plan}
          configValues={configValues}
          onClose={() => setShowViewAllModal(false)}
        />
      )}
      {showPriceDetailModal && (
        <PriceDetailModal
          plan={plan}
          onClose={() => setShowPriceDetailModal(false)}
        />
      )}
      {showQuoteModal && (
        <QuoteConfirmModal
          plan={plan}
          configValues={configValues}
          memberCount={memberCount}
          onConfirm={() => setShowQuoteModal(false)}
          onClose={() => setShowQuoteModal(false)}
          onSubmitOverride={async () => {
            const success = await contactQuote(plan.slug, plan.benefitCategory)
            if (success) {
              const res = useVisitorQuotesStore.getState().contactedSummary
              if (res) {
                // Redirect to the specifically generated quote detail page
                router.push(ROUTES.quoteDetail(locale, plan.slug))

                // Clear visitor session state without deleting the cookie
                useVisitorQuotesStore.setState({
                  mergeResult: null,
                  contactedSummary: null,
                })

                const targetQuote = res?.groups?.find(
                  (g) => g.quoteId === plan.slug
                )

                return {
                  id: plan.slug,
                  premium_total: targetQuote?.totalWithVat ?? res?.totalWithVat,
                  status: "generated",
                  documents: [
                    {
                      type: "policy_doc",
                      file_url: `${process.env.NEXT_PUBLIC_S3_URL || ""}${res?.pdfKey}`,
                    },
                  ],
                } as any
              }
            }
            throw new Error("Failed to generate quote")
          }}
        />
      )}
    </>
  )
}
